import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Building2 } from 'lucide-react';
import { getProject, getCompanies } from '../lib/api';
import { BackLink, EmptyState, LoadingScreen, SectionTag, TealButton } from '../components/common/BrandElements';

const ProjectDetailPage = () => {
  const { slug } = useParams();
  const { t, isRTL } = useLanguage();
  const [project, setProject] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectRes, companiesRes] = await Promise.all([getProject(slug), getCompanies()]);
        setProject(projectRes.data);
        setCompanies(companiesRes.data);
      } catch (error) {
        console.error('Error loading project:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug]);

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? t(company.name_ar, company.name_en) : '';
  };

  if (loading) return <LoadingScreen />;

  if (!project) {
    return (
      <div className="min-h-screen bg-raad-porcelain pt-40">
        <EmptyState
          title={t('المشروع غير موجود', 'Project Not Found')}
          action={<TealButton to="/projects">{t('العودة للمشاريع', 'Back to Projects')}</TealButton>}
        />
      </div>
    );
  }

  const images = project.images?.length ? project.images : ['/assets/raad-identity-hero.png'];

  return (
    <div className="min-h-screen bg-raad-porcelain" data-testid="project-detail-page">
      <section className="relative overflow-hidden pt-36 pb-14 lg:pt-40">
        <div className="brand-grid absolute inset-0 opacity-55" />
        <div className="container relative z-10 mx-auto px-6">
          <BackLink to="/projects" label={t('العودة للمشاريع', 'Back to Projects')} isRTL={isRTL} />
          <motion.header initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="mt-10 max-w-5xl">
            <SectionTag>{t('تفاصيل المشروع', 'Project Details')}</SectionTag>
            {project.company_id && (
              <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-black text-raad-teal shadow-soft">
                <Building2 className="h-4 w-4" />
                {getCompanyName(project.company_id)}
              </div>
            )}
            <h1 className="mt-5 text-4xl font-black leading-tight text-raad-charcoal sm:text-5xl lg:text-6xl text-balance">
              {t(project.title_ar, project.title_en)}
            </h1>
            <p className="mt-5 text-lg leading-9 text-raad-graphite/70">
              {t(project.description_ar, project.description_en)}
            </p>
          </motion.header>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {images.map((image, index) => (
              <div key={`${image}-${index}`} className={`group overflow-hidden rounded-[2rem] border border-white bg-white shadow-soft ${index === 0 ? 'md:col-span-2' : ''}`}>
                <img
                  src={image}
                  alt={`${t(project.title_ar, project.title_en)} - ${index + 1}`}
                  className={`${index === 0 ? 'h-[520px]' : 'h-80'} w-full object-cover transition-transform duration-500 group-hover:scale-105`}
                />
              </div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetailPage;
